import type { StreamingBlock } from "@storytell/streaming";
import { getBlockContent } from "@storytell/streaming";
import {
  TbAlertCircle,
  TbAlertTriangle,
  TbCheck,
  TbChevronDown,
  TbChevronRight,
  TbClipboardList,
  TbClock,
  TbTool,
  TbX,
} from "solid-icons/tb";
import { createMemo, createSignal, For, Show } from "solid-js";
import { safeParseBlockJson } from "../message/parseBlockContent";
import { useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import { taskStatusTone } from "./taskStatus";

interface ToolExecution {
  name: string;
  input: string;
  duration: number;
  success: boolean;
  error?: string;
  timestamp: string;
}

interface ExecutionStats {
  total_duration: number;
  tool_call_count: number;
  tokens_consumed: number;
  artifact_count: number;
}

interface Execution {
  task: string;
  agent_type: string;
  text_summary: string;
  tools_executed: ToolExecution[];
  stats: ExecutionStats;
  status: string;
}

interface Artifact {
  tool_call_id: string;
  tool_name: string;
  type: string;
  data: unknown;
  timestamp: string;
}

interface ToolCall {
  id: string;
  name: string;
  input: string;
  start_time: string;
  duration: number;
  success: boolean;
  error?: string;
}

interface TaskResultData {
  type: "task_result";
  version: number;
  execution: Execution;
  artifacts: Artifact[];
  tool_calls: ToolCall[];
  completed: boolean;
  duration_ms: number;
  tokens_consumed: number;
}

interface TaskRendererProps {
  block: StreamingBlock;
}

function formatDuration(nanoseconds: number): string {
  const ms = nanoseconds / 1_000_000;
  if (ms < 1000) {
    return `${Math.round(ms)}ms`;
  }
  const seconds = ms / 1000;
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}

function formatTokens(tokens: number): string {
  if (tokens < 1000) {
    return `${tokens}`;
  }
  return `${(tokens / 1000).toFixed(1)}k`;
}

export function TaskRenderer(props: TaskRendererProps) {
  const rt = useChatRuntime();

  // Single memo that checks both sources
  const data = createMemo<TaskResultData | null>(() => {
    // Try finalContent first (for completed blocks)
    if (props.block.finalContent?.type === "text" && props.block.finalContent.text) {
      const parsed = safeParseBlockJson(props.block.finalContent.text) as TaskResultData | null;
      if (parsed?.type === "task_result") return parsed;
    }
    // Try streaming content from granular store
    const content = getBlockContent(props.block.blockId);
    if (content) {
      const parsed = safeParseBlockJson(content) as TaskResultData | null;
      if (parsed?.type === "task_result") return parsed;
    }
    return null;
  });

  const isStreaming = () => !props.block.isComplete;

  const [summaryExpanded, setSummaryExpanded] = createSignal(true);
  const [timelineExpanded, setTimelineExpanded] = createSignal(false);
  const [expandedTools, setExpandedTools] = createSignal<Set<number>>(new Set());

  const status = createMemo(() => data()?.execution?.status ?? "running");

  const statusConfig = createMemo(() => {
    switch (taskStatusTone(status())) {
      case "completed":
        return {
          label: rt.t("common.completed"),
          bgColor: "bg-green-100 dark:bg-green-900/30",
          textColor: "text-green-700 dark:text-green-300",
          icon: () => <TbCheck size={14} class="text-green-600 dark:text-green-400" />,
        };
      case "partial":
        return {
          label: rt.t("task_status_partial"),
          bgColor: "bg-amber-100 dark:bg-amber-900/30",
          textColor: "text-amber-700 dark:text-amber-300",
          icon: () => <TbAlertCircle size={14} class="text-amber-600 dark:text-amber-400" />,
        };
      case "timeout":
        return {
          label: rt.t("task_status_timeout"),
          bgColor: "bg-orange-100 dark:bg-orange-900/30",
          textColor: "text-orange-700 dark:text-orange-300",
          icon: () => <TbClock size={14} class="text-orange-600 dark:text-orange-400" />,
        };
      case "cancelled":
        return {
          label: rt.t("task_status_cancelled"),
          bgColor: "bg-gray-100 dark:bg-gray-800/50",
          textColor: "text-gray-700 dark:text-gray-300",
          icon: () => <TbX size={14} class="text-gray-600 dark:text-gray-400" />,
        };
      case "error":
        return {
          label: rt.t("common.error"),
          bgColor: "bg-red-100 dark:bg-red-900/30",
          textColor: "text-red-700 dark:text-red-300",
          icon: () => <TbAlertTriangle size={14} class="text-red-600 dark:text-red-400" />,
        };
      default:
        return {
          label: rt.t("task_status_running"),
          bgColor: "bg-blue-100 dark:bg-blue-900/30",
          textColor: "text-blue-700 dark:text-blue-300",
          icon: () => <ProcessingSpinner class="size-3.5" />,
        };
    }
  });

  const toggleToolExpanded = (index: number) => {
    setExpandedTools((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  const toolCalls = createMemo(() => {
    const d = data();
    if (!d) return [];
    return d.tool_calls.length > 0 ? d.tool_calls : (d.execution?.tools_executed ?? []);
  });

  return (
    <div
      data-renderer="task-renderer"
      class="overflow-hidden rounded-xl border border-primary-border bg-panel text-base"
    >
      <Show
        when={data()}
        fallback={
          <Show when={isStreaming()}>
            <div class="flex items-center gap-2 p-3 text-sm text-secondary-text">
              <ProcessingSpinner class="size-4" />
              <span>{rt.t("task_loading")}</span>
            </div>
          </Show>
        }
      >
        {(taskData) => (
          <>
            {/* Header */}
            <div class="flex items-center justify-between border-b border-muted-border px-4 py-3">
              <div class="flex items-center gap-2">
                <TbClipboardList size={18} class="text-secondary-text" />
                <span class="text-sm font-medium text-primary-text">
                  {taskData().execution?.task || rt.t("entity.task")}
                </span>
              </div>
              <div
                class={`flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${statusConfig().bgColor} ${statusConfig().textColor}`}
              >
                {statusConfig().icon()}
                <span>{statusConfig().label}</span>
              </div>
            </div>

            {/* Text Summary Section */}
            <Show when={taskData().execution?.text_summary}>
              <div class="border-b border-muted-border">
                <button
                  type="button"
                  onClick={() => setSummaryExpanded(!summaryExpanded())}
                  class="flex w-full cursor-pointer items-center justify-between px-4 py-2 text-sm text-secondary-text transition-colors hover:bg-tile-hover"
                >
                  <Show when={summaryExpanded()} fallback={<TbChevronRight size={16} />}>
                    <TbChevronDown size={16} />
                  </Show>
                  <span class="font-medium">{rt.t("entity.summary")}</span>
                </button>
                <Show when={summaryExpanded()}>
                  <div class="px-4 pb-3 text-sm whitespace-pre-wrap text-primary-text">
                    {taskData().execution.text_summary}
                  </div>
                </Show>
              </div>
            </Show>

            {/* Tool Execution Timeline */}
            <Show when={toolCalls().length > 0}>
              <div class="border-b border-muted-border">
                <button
                  type="button"
                  onClick={() => setTimelineExpanded(!timelineExpanded())}
                  class="flex w-full cursor-pointer items-center justify-between px-4 py-2 text-sm text-secondary-text transition-colors hover:bg-tile-hover"
                >
                  <Show when={timelineExpanded()} fallback={<TbChevronRight size={16} />}>
                    <TbChevronDown size={16} />
                  </Show>
                  <div class="flex items-center gap-2">
                    <TbTool size={14} />
                    <span class="font-medium">{rt.t("task_tools_executed")}</span>
                    <span class="text-xs text-muted-text">({toolCalls().length})</span>
                  </div>
                </button>
                <Show when={timelineExpanded()}>
                  <div class="space-y-2 px-4 pb-3">
                    <For each={toolCalls()}>
                      {(tool, index) => {
                        const isExpanded = () => expandedTools().has(index());
                        const toolSuccess = () => ("success" in tool ? tool.success : true);
                        const toolDuration = () => tool.duration;
                        const toolError = () => ("error" in tool ? tool.error : undefined);

                        return (
                          <div class="overflow-hidden rounded-lg border border-muted-border">
                            <button
                              type="button"
                              onClick={() => toggleToolExpanded(index())}
                              class="flex w-full cursor-pointer items-center justify-between px-3 py-2 text-sm transition-colors hover:bg-tile-hover"
                            >
                              <Show when={isExpanded()} fallback={<TbChevronRight size={14} />}>
                                <TbChevronDown size={14} />
                              </Show>
                              <div class="flex items-center gap-2">
                                <Show
                                  when={toolSuccess()}
                                  fallback={<TbAlertTriangle size={14} class="shrink-0 text-red-500" />}
                                >
                                  <TbCheck size={14} class="shrink-0 text-green-500" />
                                </Show>
                                <span class="font-mono text-xs text-primary-text">{tool.name}</span>
                              </div>
                              <div class="flex items-center gap-2">
                                <span class="text-xs text-muted-text">{formatDuration(toolDuration())}</span>
                              </div>
                            </button>
                            <Show when={isExpanded()}>
                              <div class="border-t border-muted-border bg-inset px-3 pt-1 pb-3">
                                <Show when={tool.input}>
                                  <div class="text-xs">
                                    <span class="font-medium text-secondary-text">{rt.t("task_tool_input")}:</span>
                                    <pre class="mt-1 overflow-x-auto rounded bg-panel p-2 text-xs wrap-break-word whitespace-pre-wrap text-primary-text">
                                      {tool.input}
                                    </pre>
                                  </div>
                                </Show>
                                <Show when={toolError()}>
                                  <div class="mt-2 text-xs">
                                    <span class="font-medium text-red-500">{rt.t("common.error")}:</span>
                                    <pre class="mt-1 overflow-x-auto rounded bg-red-50 p-2 text-xs wrap-break-word whitespace-pre-wrap text-red-700 dark:bg-red-900/20 dark:text-red-300">
                                      {toolError()}
                                    </pre>
                                  </div>
                                </Show>
                              </div>
                            </Show>
                          </div>
                        );
                      }}
                    </For>
                  </div>
                </Show>
              </div>
            </Show>

            {/* Stats Footer */}
            <div class="flex items-center gap-4 bg-inset px-4 py-2 text-xs text-secondary-text">
              <Show when={taskData().duration_ms || taskData().execution?.stats?.total_duration}>
                <div class="flex items-center gap-1">
                  <TbClock size={12} />
                  <span>
                    {taskData().duration_ms
                      ? `${(taskData().duration_ms / 1000).toFixed(1)}s`
                      : formatDuration(taskData().execution.stats.total_duration)}
                  </span>
                </div>
              </Show>
              <Show when={taskData().execution?.stats?.tool_call_count}>
                <div class="flex items-center gap-1">
                  <TbTool size={12} />
                  <span>
                    {taskData().execution.stats.tool_call_count} {rt.t("task_tools")}
                  </span>
                </div>
              </Show>
              <Show when={taskData().tokens_consumed || taskData().execution?.stats?.tokens_consumed}>
                <div class="flex items-center gap-1">
                  <span>
                    {formatTokens(taskData().tokens_consumed || taskData().execution.stats.tokens_consumed)}{" "}
                    {rt.t("task_tokens")}
                  </span>
                </div>
              </Show>
              <Show when={taskData().execution?.stats?.artifact_count}>
                <div class="flex items-center gap-1">
                  <span>
                    {taskData().execution.stats.artifact_count} {rt.t("task_artifacts")}
                  </span>
                </div>
              </Show>
            </div>
          </>
        )}
      </Show>
    </div>
  );
}
